import {
  DocumentTitleOptions,
  LinkingOptions,
  NavigationContainer,
} from '@react-navigation/native';
import {
  createNativeStackNavigator,
  NativeStackNavigatorProps,
} from '@react-navigation/native-stack';
import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MainStackParams } from './common/screen_params';
import {
  BlankTv,
  BookmarkTv,
  CatalogTv,
  CollectionsTv,
  ContinueWatchDialogTv,
  DebugDialogTv,
  DetailsTv,
  FavoritesTv,
  FiltersDialogTv,
  HistoryTv,
  LoginMobile,
  LoginTv,
  MainMobile,
  MainTv,
  OptionsDialogMobile,
  OptionsDialogTv,
  ParentDialogTv,
  ParentPassDialog,
  PlayerMobile,
  PlayerTv,
  SearchChannelsResult,
  SearchEpgResult,
  SearchFilterDialog,
  SearchMoviesResult,
  SearchTv,
  SettingsTv,
  Splash,
  Tv,
} from './routes';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  TVEventControl,
  View,
} from 'react-native';
import { commonStyles } from './common/styles';
import { MainDialogMobile, MainDialogTv } from './components/main_dialog';
import { MainStackProps } from './types/screens';
import AppColors from './common/colors';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GlobalStyleWrapper } from './common/style_context';
import Utils from './utils/utils';
import { computed, reaction } from 'mobx';
import { SeasonEpisodeType, TimerType } from './types/others';
import States from './common/states';
import Constants from './utils/constants';
import useMouseControl from './common/mouse_control/mouse_control';
import Device from './common/device';
import { observer } from 'mobx-react-lite';
import * as Sentry from '@sentry/react-native';
import localization from './common/localization';
import { persistedStorage } from './common/storage';
import { TvToastProvider } from './components/tv_toast/toast_provider';
import ErrorBoundary from './components/error_boundary';
import { ToastProvider } from 'react-native-toast-notifications';
import {
  createStackNavigator,
  StackNavigatorProps,
} from '@react-navigation/stack';

Sentry.init({
  dsn: 'https://458d1ddaf3dfc411a4260136ab77df02@sentry.idc.md/3',
  release:
    Platform.OS === 'web'
      ? `${Device.version()} (${Device.buildNumber()} ${Device.os()})`
      : undefined,
});

const MainStack = Platform.isTVOS
  ? createStackNavigator<MainStackParams>()
  : createNativeStackNavigator<MainStackParams>();

const App = memo(() => {
  useEffect(() => {
    TVEventControl?.enableTVMenuKey();
    TVEventControl?.enableTVPanGesture();
    return () => {
      TVEventControl?.disableTVMenuKey();
      TVEventControl?.disableTVPanGesture();
    };
  }, []);

  return (
    <GlobalStyleWrapper>
      <AppWrapped />
    </GlobalStyleWrapper>
  );
});

const AppWrapped = observer(() => {
  const auth = computed(() => States.mainState.session !== undefined).get();
  const [isTv] = useState<boolean>(() => Device.isTv());

  const [initialScreen, setInitialScreen] = useState<keyof MainStackParams>();

  useEffect(() => {
    let timer: TimerType | undefined;
    return reaction(
      () => States.timeState.currentTime,
      () => {
        if (timer !== undefined) clearInterval(timer);
        timer = setInterval(
          () => States.timeState.incrementTimeshift(),
          Constants.timeshiftSize * 1000,
        );
      },
      { fireImmediately: true },
    );
  }, []);

  useMouseControl();

  const reset = useCallback(() => setInitialScreen(undefined), []);

  if (initialScreen === undefined) {
    return <Splash isTv={isTv} onLoaded={setInitialScreen} />;
  }

  if (isTv) {
    return (
      <ErrorBoundary reset={reset}>
        <TvToastProvider>
          <SafeAreaProvider style={styles.container}>
            <NavigationContainer
              documentTitle={documentTitle}
              linking={linkingTv}>
              <MainStack.Navigator
                initialRouteName={initialScreen}
                screenLayout={NavigatorFallback}
                screenOptions={screenOptions}>
                {TvScreens(auth)}
              </MainStack.Navigator>
              <Pressable
                focusable
                tvFocusable
                hasTVPreferredFocus
                tabIndex={0}
                style={styles.pressableFill}
              />
            </NavigationContainer>
            <CriticalErrorDialog isTv />
          </SafeAreaProvider>
        </TvToastProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary reset={reset}>
      <ToastProvider swipeEnabled={false}>
        <SafeAreaProvider>
          <NavigationContainer
            documentTitle={documentTitle}
            //@ts-ignore linkingMobile have generic type
            linking={linkingMobile}>
            <MainStack.Navigator
              initialRouteName={initialScreen}
              screenLayout={NavigatorFallback}
              screenOptions={screenOptions}>
              {MobileScreens(auth)}
            </MainStack.Navigator>
          </NavigationContainer>
          <CriticalErrorDialog />
        </SafeAreaProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
});

const NavigatorFallback: NativeStackNavigatorProps['screenLayout'] &
  StackNavigatorProps['screenLayout'] = ({ children }) => {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
};

const Loading = memo(() => {
  return (
    <View style={styles.loadingContainer}>
      <ActivityIndicator color={AppColors.mainBlue} size={'large'} />
    </View>
  );
});

const TvScreens = (auth?: boolean) => {
  return (
    <MainStack.Group screenOptions={tvGropsScreenOptions}>
      {!auth ? (
        <MainStack.Screen
          name='LoginTv'
          component={LoginTv}
          options={loginScreenOptions}
        />
      ) : (
        <>
          <MainStack.Screen name='MainTv' component={MainTv} />
          <MainStack.Screen name='Tv' component={Tv} />
          <MainStack.Screen
            name='CatalogTv'
            component={CatalogTv}
            initialParams={Utils.emptyObject}
          />
          <MainStack.Screen name='SearchTv' component={SearchTv} />
          <MainStack.Screen
            name='SearchMoviesResult'
            component={SearchMoviesResult}
          />
          <MainStack.Screen
            name='SearchChannelsResult'
            component={SearchChannelsResult}
          />
          <MainStack.Screen
            name='SearchEpgResult'
            component={SearchEpgResult}
          />
          <MainStack.Screen name='BookmarkTv' component={BookmarkTv} />
          <MainStack.Screen name='HistoryTv' component={HistoryTv} />
          <MainStack.Screen name='FavoritesTv' component={FavoritesTv} />
          <MainStack.Screen name='SettingsTv' component={SettingsTv} />

          <MainStack.Screen name='CollectionsTv' component={CollectionsTv} />
          <MainStack.Screen name='DetailsTv' component={DetailsTv} />
          <MainStack.Screen name='PlayerTv' component={PlayerTv} />

          <MainStack.Screen name='BlankTv' component={BlankTv} />
        </>
      )}
      <MainStack.Group screenOptions={tvDialogsScreenOptions}>
        <MainStack.Screen name='MainDialogTv' component={MainDialogTvWrapper} />
        <MainStack.Screen name='FilterDialogTv' component={FiltersDialogTv} />
        <MainStack.Screen name='OptionsDialogTv' component={OptionsDialogTv} />
        <MainStack.Screen
          name='SearchFilterDialog'
          component={SearchFilterDialog}
        />
        <MainStack.Screen
          name='ContinueWatchDialog'
          component={ContinueWatchDialogTv}
        />
        <MainStack.Screen name='DebugDialogTv' component={DebugDialogTv} />
        <MainStack.Screen name='ParentDialogTv' component={ParentDialogTv} />
        <MainStack.Screen
          name='ParentPassDialogTv'
          component={ParentPassDialog}
        />
      </MainStack.Group>
    </MainStack.Group>
  );
};

const MobileScreens = (auth?: boolean) => {
  return (
    <MainStack.Group screenOptions={mobileGropsScreenOptions}>
      {!auth ? (
        <MainStack.Screen name='LoginMobile' component={LoginMobile} />
      ) : (
        <>
          <MainStack.Screen name='MainMobile' component={MainMobile} />
          <MainStack.Screen
            name='PlayerMobile'
            component={PlayerMobile}
            options={mobilePlayerScreenOptions}
          />
          <MainStack.Screen
            name='BlankMobile'
            component={BlankMobile}
            options={mobileBlankScreenOptions}
          />
        </>
      )}
      <MainStack.Group screenOptions={mobileDialogsScreenOptions}>
        <MainStack.Screen
          name='MainDialogMobile'
          component={MainDialogMobileWrapper}
        />
        <MainStack.Screen
          name='OptionsDialogMobile'
          component={OptionsDialogMobile}
        />
      </MainStack.Group>
    </MainStack.Group>
  );
};

const BlankMobile = memo<MainStackProps<'BlankMobile'>>(({ navigation }) => {
  useEffect(() => {
    const t = setTimeout(() => navigation.pop());
    return () => clearTimeout(t);
  }, []);

  const onPress = useCallback(() => navigation.pop(), []);

  return <Pressable onPress={onPress} style={commonStyles.fill} />;
});

const CriticalErrorDialog = observer<{ isTv?: boolean }>(({ isTv }) => {
  const errorCode = States.mainState.criticalError;

  const error = useMemo(
    () =>
      errorCode !== undefined
        ? localization.errCodeToMsg(errorCode)
        : undefined,
    [errorCode],
  );

  const submit = useCallback((action?: string) => {
    if (action === 'cancel') {
      persistedStorage.delete('ssid');
      persistedStorage.delete('ssidName');

      States.mainState.logout(true);
      States.mainState.criticalError = undefined;
    }
  }, []);

  return (
    <Modal
      transparent
      visible={error !== undefined}
      backdropColor={AppColors.semiBlack}>
      {isTv ? (
        <MainDialogTv
          title={localization.error.error}
          text={error ?? ''}
          actions={criticalErrorActions}
          defaultAction='cancel'
          submit={submit}
        />
      ) : (
        <MainDialogMobile
          title={localization.error.error}
          text={error ?? ''}
          actions={criticalErrorActions}
          defaultAction='cancel'
          submit={submit}
        />
      )}
    </Modal>
  );
});

type DialogTvProps = MainStackProps<'MainDialogTv'>;

const MainDialogTvWrapper = memo<DialogTvProps>(
  ({ navigation, route: { params } }) => {
    const submit = useCallback((action?: string) => {
      const routes = navigation.getState().routes;
      const route = routes[routes.length - 2].name;

      //@ts-ignore not all route support dialog action
      navigation.popTo(route, { action }, { merge: true });
    }, []);
    return <MainDialogTv {...params} submit={submit} />;
  },
);

type DialogMobileProps = MainStackProps<'MainDialogMobile'>;

const MainDialogMobileWrapper = memo<DialogMobileProps>(
  ({ navigation, route: { params } }) => {
    const submit = useCallback((action?: string) => {
      const routes = navigation.getState().routes;
      const route = routes[routes.length - 2].name;

      //@ts-ignore not all route support dialog action
      navigation.popTo(route, { action }, { merge: true });
    }, []);
    return <MainDialogMobile {...params} submit={submit} />;
  },
);

type ScreenOptionsType = NativeStackNavigatorProps['screenOptions'] &
  StackNavigatorProps['screenOptions'];

const documentTitle: DocumentTitleOptions = {
  enabled: true,
  formatter: () => 'IDC IPTV',
};
const screenOptions: ScreenOptionsType = {
  headerShown: false,
  title: 'IDC IPTV',
  contentStyle: {
    backgroundColor: 'black',
    overflow: 'hidden',
  },
  cardStyle: { backgroundColor: 'black' },
  keyboardHandlingEnabled: true,
};
const loginScreenOptions: ScreenOptionsType = {
  contentStyle: {
    backgroundColor: AppColors.mainBlack,
  },
};

const tvGropsScreenOptions: ScreenOptionsType = {
  animation: 'none',
  navigationBarHidden: true,
  statusBarHidden: true,
};
const tvDialogsScreenOptions: ScreenOptionsType = {
  presentation: 'transparentModal',
  contentStyle: { backgroundColor: AppColors.semiBlack },
  cardStyle: { backgroundColor: AppColors.semiBlack },
};

const mobileGropsScreenOptions: ScreenOptionsType = {
  orientation: 'portrait',
  contentStyle: { backgroundColor: AppColors.mobileBlack, overflow: 'hidden' },
};
const mobileDialogsScreenOptions: ScreenOptionsType = {
  animation: 'fade',
  presentation: 'transparentModal',
  contentStyle: { backgroundColor: AppColors.semiBlack },
};
const mobilePlayerScreenOptions: ScreenOptionsType = {
  orientation: 'landscape',
  gestureEnabled: false,
  autoHideHomeIndicator: true,
  statusBarHidden: true,
  navigationBarHidden: true,
  contentStyle: { backgroundColor: 'black' },
};
const mobileBlankScreenOptions: ScreenOptionsType = {
  animation: 'none',
  gestureEnabled: false,
  presentation: 'transparentModal',
  contentStyle: { backgroundColor: 'transparent' },
};

function strToNumberArr(value: string) {
  return value
    .split(',')
    .map(e => +e)
    .filter(e => !!e);
}
const linkingTv: LinkingOptions<MainStackParams> = {
  prefixes: [],
  config: {
    screens: {
      LoginTv: 'login',
      MainTv: 'main',
      Tv: { path: 'tv/:id?', parse: { id: Number, date: Number } },
      CatalogTv: {
        path: 'catalog',
        parse: {
          category: strToNumberArr,
          distributor: strToNumberArr,
          genre: strToNumberArr,
          country: strToNumberArr,
          year: strToNumberArr,
          sort: strToNumberArr,
        },
      },
      SearchTv: 'search',
      SettingsTv: 'settings',

      BookmarkTv: 'bookmark',
      FavoritesTv: 'bookmark/favorites',
      HistoryTv: 'bookmark/history',

      CollectionsTv: 'collections',
      SearchMoviesResult: {
        path: 'result/:category',
        parse: { category: Number, tab: Number },
      },
      SearchChannelsResult: 'channels-search',
      SearchEpgResult: 'epg-search',

      DetailsTv: { path: 'movie/:id', parse: { id: Number, tab: Number } },
      PlayerTv: {
        path: 'player',
        parse: {
          info: value => {
            if (value.length === 0) return undefined;
            const items = value
              .slice(1)
              .split('e')
              .map(e => +e);
            return { season: items[0], episode: items[1] };
          },
          item: () => undefined,
          recommendations: () => undefined,
        },
        stringify: {
          item: () => '',
          recommendations: () => '',
          info: (value: SeasonEpisodeType) =>
            value ? `s${value.season}e${value.episode}` : '',
        },
      },
      BlankTv: {
        path: 'blank',
        parse: {
          id: Number,
          position: Number,
          info: value => {
            if (value.length === 0) return undefined;
            const items = value
              .slice(1)
              .split('e')
              .map(e => +e);
            return { season: items[0], episode: items[1] };
          },
        },
        stringify: {
          info: (value: SeasonEpisodeType) =>
            value ? `s${value.season}e${value.episode}` : '',
        },
      },
    },
  },
};

const linkingMobile: LinkingOptions<{}> = {
  prefixes: [],
  config: {
    screens: {
      MainMobile: {
        path: '',
        screens: {
          Main: {
            path: 'main',
            screens: {
              Root: '',
              Category: {
                path: 'category/:category',
                parse: {
                  category: Number,
                },
              },
              Collections: 'collections',
              Details: {
                path: 'movie/:id',
                parse: {
                  id: Number,
                },
              },
            },
          },
          Tv: 'tv',
          Catalog: {
            path: 'catalog',
            screens: {
              Root: '',
              Details: {
                path: 'movie/:id',
                parse: {
                  id: Number,
                },
              },
            },
          },
          Search: {
            path: 'search',
            screens: {
              Root: '',
              MoviesResult: 'movies',
              Details: {
                path: 'movie/:id',
                parse: {
                  id: Number,
                },
              },
            },
          },
          More: {
            path: 'more',
            screens: {
              Root: '',
              Favorites: 'favorites',
              History: 'history',
              Settings: 'settings',
              ParentControl: 'settings/parent',
              Details: {
                path: 'movie/:id',
                parse: {
                  id: Number,
                },
              },
            },
          },
        },
      },
    },
  },
};

const criticalErrorActions = [
  { text: localization.general.ok, action: 'cancel' },
];

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'black',
  },
  pressableFill: {
    ...commonStyles.fill,
    pointerEvents: 'none',
  },
  loadingContainer: {
    ...commonStyles.fill,
    justifyContent: 'center',
  },
});

export default Sentry.wrap(App);
